import { Component, Input, ViewChild } from '@angular/core';
import { ChartBaseComponent } from '../base/chart-base/chart-base.component';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import { CommonService } from '../../../../shared/services/common.service';
import { PageEvent } from '@angular/material/paginator';
import { GroupedBarChartComponent } from '../../../../shared/components/charts/grouped-bar-chart/grouped-bar-chart.component';
import { BarChartComponent } from '../../../../shared/components/charts/bar-chart/bar-chart.component';
import { GroupedBarChartModel } from '../../../../../models/grouped-bar-chart.model';
import { BarChartModel } from '../../../../../models/bar-chart.model';
import { WeeklyFoodChartTableData } from '../../models/fetch-weekly-food-chart-table-data.model';
import {
  dailyFoodChartData,
  weeklyFoodChartData,
  weeklyFoodChartTableData,
} from '../../core/progress.selectors';
import {
  FetchDailyFoodChartDataRequest,
  FetchWeeklyFoodChartDataRequest,
  FetchWeeklyFoodChartTableDataRequest,
} from '../../core/progress.actions';
import moment from 'moment';
import { PrintService } from '../../../../shared/services/print.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-red-light-food-chart',
  templateUrl: './red-light-food-chart.component.html',
  styleUrls: ['./red-light-food-chart.component.css'],
})
export class RedLightFoodChartComponent extends ChartBaseComponent {
  // for print
  @Input() printData: any;

  onDestroy = new Subject<void>();
  form = this.formBuilder.group<any>({});

  // Grouped bar chart
  @ViewChild(GroupedBarChartComponent, { static: false })
  groupedBarChartComponent: GroupedBarChartComponent;
  groupedBarChartData: { loading: boolean; data: GroupedBarChartModel };

  // bar chart
  @ViewChild(BarChartComponent, { static: false })
  barChartComponent: BarChartComponent;
  barChartData: { loading: boolean; data: BarChartModel };
  chartTableData: { loading: boolean; data: WeeklyFoodChartTableData };

  dailyChart = true;
  showTarget = false;

  fromDate: string;
  toDate: string;
  columns: any;
  initialLoad = true;
  additionalText = '';
  sortColumn = 'weekNumber';
  sortOrder: 'asc' | 'desc' = 'asc';

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private printService: PrintService,
    private datePipe: DatePipe,
  ) {
    super();
  }

  override onDateChange(data: { fromDate: string; toDate: string }) {
    this.fromDate = data.fromDate;
    this.toDate = data.toDate;

    // fetch chart details
    this.fetchChartDetails();
  }

  override onPrint(complete: () => void) {
    if (!(this.groupedBarChartData?.data || this.barChartData?.data)) {
      this.commonService.notification('Nothing to print.', 'danger');
      complete();
      return;
    }
    // this.commonService.printElement('bmi-chart-container');
    this.printService.open({
      component: RedLightFoodChartComponent,
      data: {
        fromDate: this.fromDate,
        toDate: this.toDate,
        memberId: this.memberId,
        groupId: this.groupId,
        dailyChart: this.dailyChart,
        showTarget: this.showTarget,
        userName: this.getUserName(this.profile),
        title: this.getPageTitle({
          profile: this.profile,
          fromDate: this.fromDate,
          toDate: this.toDate,
          chartName: this.chartName,
        }),
      },
      onClose: complete,
    });
  }

  ngOnInit() {

    this.columns = [
      'week',
      'dateRange',
      'sessions',
      'weekTotal',
      ...(this.isAdult ? []: ['weekTarget']),
      'dayTotal',
    ];

    if (this.printData) {
      this.fromDate = this.printData['fromDate'];
      this.toDate = this.printData['toDate'];
      this.dailyChart = this.printData['dailyChart'];
      this.memberId = this.printData['memberId'];
      this.groupId = this.printData['groupId'];
      this.showTarget = this.printData['showTarget'];

      // append date to title
      if (this.fromDate && this.toDate) {
        this.additionalText = `[${moment(this.fromDate).format('MMM DD, YYYY')} - ${moment(this.toDate).format('MMM DD, YYYY')}]`;
      } else if (this.fromDate) {
        this.additionalText = `[From ${moment(this.fromDate).format('MMM DD, YYYY')}]`;
      } else if (this.toDate) {
        this.additionalText = `[Till ${moment(this.toDate).format('MMM DD, YYYY')}]`;
      }
    }
    this.store
      .select(dailyFoodChartData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        // prepare chart data
        data = JSON.parse(JSON.stringify(data));

        data?.data?.data?.forEach((item) => {
          item.title = `W${item.weekNumber}`;
          item.smallTitle = `${item.weekNumber}`;
          item.subtitle = `${this.datePipe.transform(item.dateRange.start, 'MM/dd')} - ${this.datePipe.transform(item.dateRange.end, 'MM/dd')}`;
        });

        this.groupedBarChartData = data;

        // check is it for print
        if (!this.groupedBarChartData?.loading && this.printData) {
          this.checkDataReadyForPrint();
        }
      });

    this.store
      .select(weeklyFoodChartData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        // prepare chart data
        data = JSON.parse(JSON.stringify(data));
        data?.data?.data?.forEach((item) => {
          item.title = `${item.weekNumber}`;
          item.subtitle = `${this.datePipe.transform(item.dateRange.start, 'YYYY-MM-dd')} - ${this.datePipe.transform(item.dateRange.end, 'YYYY-MM-dd')}`;
        });

        this.barChartData = data;

        // check is it for print
        if (!this.barChartData?.loading && this.printData) {
          this.checkDataReadyForPrint();
        }
      });

    this.store
      .select(weeklyFoodChartTableData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        this.chartTableData = data;

        // check is it for print
        if (!this.chartTableData?.loading && this.printData) {
          this.checkDataReadyForPrint();
        }

        if (!data.loading && data.data?.hasNext) {
          setTimeout(() => {
            // check window height for infinite scroll
            this.commonService.checkInfiniteScrollHeightAndCallNext(
              'table-wrapper',
              () => {
                this.onScroll();
              },
            );
          }, 0);
        }
      });

    if (this.printData) {
      this.fetchChartDetails();
    }
  }

  fetchChartDetails() {
    if (this.dailyChart) {
      // fetch chart data
      this.store.dispatch(
        FetchDailyFoodChartDataRequest({
          payload: {
            ...(this.memberId ? { userId: this.memberId } : {}),
            ...(this.groupId ? { groupId: this.groupId } : {}),
            from: moment(this.fromDate).format('YYYY-MM-DD'),
            to: moment(this.toDate).format('YYYY-MM-DD'),
            chartType: 1,
          },
          forPrint: !!this.printData,
        }),
      );
    } else {
      // fetch chart data
      this.store.dispatch(
        FetchWeeklyFoodChartDataRequest({
          payload: {
            ...(this.memberId ? { userId: this.memberId } : {}),
            ...(this.groupId ? { groupId: this.groupId } : {}),
            from: moment(this.fromDate).format('YYYY-MM-DD'),
            to: moment(this.toDate).format('YYYY-MM-DD'),
            chartType: 2,
          },
          forPrint: !!this.printData,
        }),
      );
    }

    // fetch chart table data
    if (this.printData) {
      this.fetchTableData(1, -1);
    } else {
      this.fetchTableData();
    }
  }

  toggleChart(value) {
    this.dailyChart = value.value;
    if (this.dailyChart) {
      this.showTarget = false;
    }
    this.fetchChartDetails();
  }

  toggleTarget() {
    if (this.dailyChart) {
      this.groupedBarChartComponent?.toggleTargetLine();
    } else {
      this.barChartComponent?.toggleTargetLine();
    }
  }

  fetchTableData(pageNumber = 1, limit = 10) {
    // fetch chart table data
    this.store.dispatch(
      FetchWeeklyFoodChartTableDataRequest({
        payload: {
          ...(this.memberId ? { userId: this.memberId } : {}),
          ...(this.groupId ? { groupId: this.groupId } : {}),
          from: this.fromDate
            ? moment(this.fromDate).format('YYYY-MM-DD')
            : this.fromDate,
          to: this.toDate
            ? moment(this.toDate).format('YYYY-MM-DD')
            : this.toDate,
          pageNumber,
          limit,
          chartType: this.dailyChart ? 1 : 2,
          sortColumn: this.sortColumn,
          sortOrder: this.sortOrder,
        },
        forPrint: !!this.printData,
      }),
    );
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      // Toggle sort order
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to asc
      this.sortColumn = column;
      this.sortOrder = 'asc';
    }
    // Fetch data with new sorting
    if (this.printData) {
      this.fetchTableData(1, -1);
    } else {
      this.fetchTableData();
    }
  }

  onScroll() {
    if (!this.chartTableData?.loading && this.chartTableData?.data?.hasNext) {
      this.fetchTableData(
        this.chartTableData?.data?.pageNumber + 1,
        this.chartTableData?.data?.limit,
      );
    }
  }

  checkDataReadyForPrint() {
    if (
      ((this.dailyChart && this.groupedBarChartData?.data) ||
        (!this.dailyChart && this.barChartData?.data)) &&
      this.chartTableData?.data?.list?.length
    ) {
      if (this.initialLoad) {
        this.initialLoad = false;
        return;
      }
      this.printService.start(this.printData?.title);
    }
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
